import React, { useEffect, useRef } from 'react'
import type { SpellcheckWord } from '../Types/SpellcheckTypes'
import { useTranslation } from 'react-i18next'
import {
  ClickAwayListener,
  Grow,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography
} from '@mui/material'

interface SpellcheckMenuProps {
  word: SpellcheckWord;
  position: { top: number; left: number };
  onSelectSuggestion: (suggestion: string) => void;
  onClose: () => void;
}

/**
 * Component to display spelling suggestions menu
 */
const SpellcheckMenu: React.FC<SpellcheckMenuProps> = ({
  word,
  position,
  onSelectSuggestion,
  onClose,
}) => {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)
  const suggestions = word.suggestions || []

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  if (suggestions.length === 0) {
    return null
  }

  // Position the menu using fixed positioning instead of Popper
  const menuStyle = {
    position: 'absolute' as const,
    // top: position.top + 'px',
    // left: position.left + 'px',
    zIndex: 1300
  };

  return (
    <div ref={menuRef} style={menuStyle}>
      <Grow
        in={true}
        style={{ transformOrigin: 'top left' }}
      >
        <Paper elevation={3} sx={{ width: 250, maxWidth: '95vw' }}>
          <ClickAwayListener onClickAway={onClose}>
            <div>
              <Typography
                variant="subtitle2"
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.100',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {t('SuggestionsFor')} <strong>{word.word}</strong>
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', cursor: 'pointer' }}>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={index}
                    onClick={() => onSelectSuggestion(suggestion)}
                  >
                    <ListItemText primary={suggestion} />
                  </ListItem>
                ))}
              </List>
            </div>
          </ClickAwayListener>
        </Paper>
      </Grow>
    </div>
  )
}

export default SpellcheckMenu
